import React, { useState } from 'react';
import { Tabs, DashboardFrame, Modal } from '../ui';
import OverviewTab from './OverviewTab';
import ChannelProfilePanel from './ChannelProfilePanel';
import AskTab from './AskTab';
import CrmTab from './CrmTab';
import DirectSoldAdsTab from '../Admin/DirectSoldAdsTab';

// Current Campaigns moved to the Ad Ops dashboard (Oct 2026).
type TabKey = 'overview' | 'channels' | 'ad-detail' | 'crm' | 'ask';

const TABS: { value: TabKey; label: string }[] = [
  { value: 'overview',  label: 'Network Overview' },
  { value: 'channels',  label: 'Channel Profile' },
  { value: 'ad-detail', label: 'Ad Detail' },
  { value: 'crm',       label: 'Channel CRM' },
  { value: 'ask',       label: 'Ask Genius' },
];

const GUIDE_URL = `${process.env.PUBLIC_URL || ''}/channel-intelligence-guide.html`;
const GUIDE_PDF_URL = `${process.env.PUBLIC_URL || ''}/channel-intelligence-guide.pdf`;

const ChannelIntelligenceDashboard: React.FC<{ userOrganization?: string }> = ({ userOrganization }) => {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [guideOpen, setGuideOpen] = useState(false);

  const renderTab = () => {
    switch (activeTab) {
      case 'overview':  return <OverviewTab />;
      case 'channels':  return <ChannelProfilePanel />;
      case 'ad-detail': return <DirectSoldAdsTab />;
      case 'crm':       return <CrmTab />;
      case 'ask':       return <AskTab userOrganization={userOrganization} />;
      default:          return null;
    }
  };

  return (
    <DashboardFrame
      eyebrow="Channel Intelligence · Unified per-channel view"
      title="Channel Intelligence"
      topBarRight={
        <button
          type="button"
          className="ci-help-btn"
          onClick={() => setGuideOpen(true)}
          title="View the Channel Intelligence guide"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M9.2 9.2a2.8 2.8 0 1 1 3.6 2.7c-.8.3-1.3 1-1.3 1.8v.3" />
            <path d="M12 17h.01" />
          </svg>
          <span>Guide</span>
        </button>
      }
      tabs={
        <Tabs<TabKey>
          value={activeTab}
          onChange={setActiveTab}
          variant="underlined"
          options={TABS}
        />
      }
    >
      {renderTab()}

      <Modal
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        width="min(1100px, 95vw)"
        eyebrow="Team Onboarding · Capability Sheet"
        title="Channel Intelligence — Guide"
        ariaLabel="Channel Intelligence guide"
        footer={
          <>
            <a className="ci-help-btn" href={GUIDE_PDF_URL} download="Channel-Intelligence-Guide.pdf">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3v12" />
                <path d="M7 10l5 5 5-5" />
                <path d="M5 21h14" />
              </svg>
              <span>Download PDF</span>
            </a>
            <button type="button" className="ci-help-btn" onClick={() => setGuideOpen(false)}>Close</button>
          </>
        }
      >
        <iframe
          title="Channel Intelligence guide"
          src={GUIDE_URL}
          style={{
            display: 'block',
            width: '100%',
            height: '72vh',
            minHeight: 420,
            border: '1px solid var(--border-1)',
            borderRadius: 'var(--radius-md)',
            background: '#fff',
          }}
        />
      </Modal>
    </DashboardFrame>
  );
};

export default ChannelIntelligenceDashboard;
